/**
 * The `Order` domain Sparta stamps on foreman job list rows, shared by web and NativeScript.
 *
 * Why this is shared rather than an inline range check: the lower bound is owned by the API.
 * `NonBillableDefaultJobListPin.PinnedOrder` (Sparta,
 * `UPS.Sparta.Services/Timekeeping/NonBillableDefaultJobListPin.cs`) pins the employee's
 * Viewpoint non-billable default job BELOW Project Manager so the locked leave panel seeds at
 * the top of the list. The NativeScript Individual Time Entry list carried its own hand-written
 * `order >= 1 && order <= 5`, so the one row the pin exists to guarantee was the one row the
 * range excluded, and nothing connected the two numbers.
 *
 * Keep these constants in step with the C# side. The assignment sites are
 * `ForemanJobRepository.GetForemanJobDetailList` (1 PM / 2 favorite / 3 dispatched /
 * 4 manpower), the foreman-created projection (5), the unmapped default (5), and the pin (0).
 */

/** Pinned Viewpoint non-billable default job. Mirrors `NonBillableDefaultJobListPin.PinnedOrder`. */
export const FOREMAN_JOB_ORDER_PINNED_NON_BILLABLE = 0;

/** Job the user is Project Manager on. Lowest claim-predicate order. */
export const FOREMAN_JOB_ORDER_PROJECT_MANAGER = 1;

/** Foreman-created job, and the value Sparta falls back to for an unmapped job. */
export const FOREMAN_JOB_ORDER_FOREMAN_CREATED = 5;

/**
 * Whether a job row carries an `Order` that puts it on the user's foreman job list.
 *
 * `order` is not on the shared `ForemanJobDetail` type, it is read off the payload, and
 * persisted rows written by older app versions can carry anything. So the check is
 * `typeof === 'number'` with no coercion: `''`, `'0'` and `false` would all coerce into the
 * range and admit a row past the claim gate.
 *
 * Fails CLOSED on an unrecognized order. A row Sparta did not stamp is not a claim.
 */
export function isOnUserForemanJobList(order: unknown): boolean {
  return (
    typeof order === 'number' &&
    Number.isFinite(order) &&
    order >= FOREMAN_JOB_ORDER_PINNED_NON_BILLABLE &&
    order <= FOREMAN_JOB_ORDER_FOREMAN_CREATED
  );
}
